import { AR_INTL_LOCALE, isArabicUi } from "@/shared/config/arabicUi";
import type { AchForm, AchLang, AchText, AchUiLang } from "../model/types";
import { arabicCatalogText } from "./arCatalog";

/** Языки публичного каталога (site_server build_catalog): у арабского свои тексты — lib/arCatalog.ts. */
export const ACH_LANGS: readonly AchLang[] = ["ru", "en", "de", "es", "zh"];

/** Язык сайта (первый сегмент адреса) → язык каталога: cn → zh, незнакомый (ar и др.) → en. Для сервера и кэшей. */
export const achLang = (siteLang: string | null | undefined): AchLang => {
    const l = String(siteLang || "").toLowerCase();
    if (l === "cn" || l === "zh") return "zh";
    return (ACH_LANGS as readonly string[]).includes(l) ? (l as AchLang) : "en";
};

/**
 * Язык текстов на экране: как achLang, но ar — арабский, когда включён арабский интерфейс
 * (рубильник или предпросмотр на этом устройстве); иначе /ar — en, как сегодня.
 */
export const achUiLang = (siteLang: string | null | undefined): AchUiLang => {
    const l = String(siteLang || "").toLowerCase();
    return l === "ar" && isArabicUi(l) ? "ar" : achLang(l);
};

const LOCALE: Record<AchUiLang, string> = { ru: "ru-RU", en: "en-US", de: "de-DE", es: "es-ES", zh: "zh-CN", ar: AR_INTL_LOCALE };

export const achLocale = (lang: AchUiLang): string => LOCALE[lang] || "en-US";

/**
 * Текст на языке человека; у названий — в форме того, кого показываем
 * (ru_f/de_f/es_f/ar_f при f, иначе мужская). Нет языка — en, потом ru.
 */
export const pickText = (text: AchText | undefined, lang: AchUiLang, form?: AchForm | null): string => {
    if (!text) return "";
    if (lang === "ar") {
        // каталог: арабский — рядом (lib/arCatalog), свои словари фичи — прямо в тексте (text.ar)
        const a = arabicCatalogText(text);
        const v = (form === "f" && (a?.ar_f || text.ar_f)) || a?.ar || text.ar;
        if (v) return v;
    }
    const female = form === "f" ? text[`${lang}_f`] : undefined;
    return female || text[lang] || text.en || text.ru || "";
};

/**
 * Число по правилам языка (Intl.NumberFormat): 30 000 · 30,000 · 30.000.
 * Неразрывный пробел Intl (ru) — обычным, как у остальных чисел сайта
 * (pointsPulse formatPoints) и в эталонных строках карточки.
 */
export const formatNumber = (n: number, lang: AchUiLang, maxFraction = 0): string =>
    new Intl.NumberFormat(achLocale(lang), { maximumFractionDigits: maxFraction }).format(n).replace(/[  ]/g, " ");

/** Категория множественного числа (one/few/many/other) — для «баллов» и единиц каталога. */
export const pluralOf = (n: number, lang: AchUiLang): string => new Intl.PluralRules(achLocale(lang)).select(n);
